import { showLoading, hideLoading } from 'react-redux-loading-bar';
import React from 'react';
import { withTranslation } from 'react-i18next';
import { CreateUpdateModalData, IProps, IState } from './types';
import store from '../../../../store';
import CustomFieldHeader from './components/custom-field-header';
import CustomFieldContent from './components/custom-field-content';
import { RequestStatus } from '../../../../shared/enums/request-status';
import toaster from '../../../../shared/toaster';
import DeleteCustomFieldModal from './components/delete-custom-field-modal';
import { ProspectType } from '../../enums/prospect-type';
import CreateProspectCustomFieldModal from './components/generic-create-update-custom-field-modal/prospect-modal/create-prospect-custom-field-modal';
import UpdateProspectCustomFieldModal from './components/generic-create-update-custom-field-modal/prospect-modal/update-prospect-custom-field-modal';
import hasResource from '../../../../shared/utils/access-control/has-resource';
import { ResourceIdentifiers } from '../../../../shared/utils/access-control/enums/resource-identifiers';
import { FieldContentTabs } from './components/custom-field-content/types';
import { getIsRequestPending } from '../../../../shared/utils';
import { FieldType } from '../../enums/field';
import DeleteOptionModal from './components/generic-create-update-custom-field-modal/delete-option-modal';

class CustomField extends React.Component<IProps, IState> {
  constructor(props) {
    super(props);
    this.state = {
      actionCustomField: null,
      showDeleteCustomFieldModal: false,
      showCreateProspectCustomFieldModal: false,
      showUpdateProspectCustomFieldModal: false,
      activeTabKey: FieldContentTabs.CustomFields,
      showDeleteOptionModal: false,
    };
  }

  componentDidUpdate(prevProps: Readonly<IProps>, prevState: Readonly<IState>) {
    const {
      isLoading,
      createCustomFieldRequestStatus,
      updateCustomFieldRequestStatus,
      deleteCustomFieldRequestStatus,
    } = this.props;
    const { activeTabKey } = this.state;

    if (isLoading !== prevProps.isLoading) {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      isLoading ? store.dispatch(showLoading()) : store.dispatch(hideLoading());
    }

    if (
      createCustomFieldRequestStatus !==
      prevProps.createCustomFieldRequestStatus
    ) {
      this.createProspectCustomFieldRefresh();
    }

    if (
      updateCustomFieldRequestStatus !==
      prevProps.updateCustomFieldRequestStatus
    ) {
      this.updateProspectCustomFieldRefresh();
    }

    if (
      deleteCustomFieldRequestStatus !==
      prevProps.deleteCustomFieldRequestStatus
    ) {
      this.deleteCustomFieldRefresh();
    }

    if (activeTabKey !== prevState.activeTabKey) {
      this.getFields();
    }
  }

  componentWillUnmount = () => {
    const { resetCustomFieldsData } = this.props;
    resetCustomFieldsData();
  };

  onCreateProspectCustomFieldSubmit = (payload: CreateUpdateModalData) => {
    const { sendCreateCustomFieldRequest } = this.props;
    const fallbackText =
      payload.type === FieldType.Date || payload.type === FieldType.Dropdown
        ? ''
        : payload.fallbackText || '';
    sendCreateCustomFieldRequest({
      prospectType: ProspectType.Contact,
      fieldType: payload.type,
      label: payload.label,
      fallbackText,
      ...(payload.type === FieldType.Dropdown && {
        metadata: { options: payload.options },
      }),
      ...(payload.type === FieldType.Currency && {
        metadata: { currencyCode: payload.currencyCode },
      }),
    });
  };

  onUpdateProspectCustomFieldSubmit = (payload: CreateUpdateModalData) => {
    const { sendUpdateCustomFieldRequest } = this.props;
    const { actionCustomField } = this.state;

    // Create an optionValueMap
    const optionValueMap = actionCustomField.options.reduce(
      (acc: Record<string, string>, option: string, index: number) => {
        const newValue = payload.options[index];
        if (option !== newValue) {
          acc[option] = newValue;
        }
        return acc;
      },
      {},
    );

    sendUpdateCustomFieldRequest(actionCustomField.id, {
      label: payload.label,
      fallbackText: payload.fallbackText ? payload.fallbackText : '',
      ...(payload.type === FieldType.Dropdown && {
        metadata: { options: payload.options },
      }),
      ...(payload.type === FieldType.Currency && {
        metadata: { currencyCode: payload.currencyCode },
      }),
      optionValueMap,
    });
  };

  showCreateProspectCustomFieldModal = () => {
    hasResource(ResourceIdentifiers.FIELDS_CREATE) &&
      this.setState({
        actionCustomField: null,
        showCreateProspectCustomFieldModal: true,
      });
  };

  hideCreateProspectCustomFieldModal = () => {
    this.setState({
      actionCustomField: null,
      showCreateProspectCustomFieldModal: false,
    });
  };

  showUpdateProspectCustomFieldModal = (customField) => {
    this.setState({
      actionCustomField: {
        ...customField,
        currencyCode: customField.fieldMetadata?.metadata?.currencyCode,
        options: customField.fieldMetadata?.metadata?.options || [],
      },
      showUpdateProspectCustomFieldModal: true,
    });
  };

  hideUpdateProspectCustomFieldModal = () => {
    this.setState({
      actionCustomField: null,
      showUpdateProspectCustomFieldModal: false,
    });
  };

  showProspectCustomFieldDeleteModal = (customField) => {
    this.setState({
      actionCustomField: {
        ...customField,
        selectedOptionForDeletion: null, // Initialize as null when opening the modal
      },
      showDeleteCustomFieldModal: true,
    });
  };

  showAccountCustomFieldDeleteModal = (customField) => {
    this.setState({
      actionCustomField: customField,
      showDeleteCustomFieldModal: true,
    });
  };

  hideDeleteCustomFieldModal = () => {
    this.setState({
      actionCustomField: null,
      showDeleteCustomFieldModal: false,
    });
  };

  showDeleteOptionModal = (option) => {
    this.setState((prevState) => ({
      actionCustomField: {
        ...prevState.actionCustomField,
        selectedOptionForDeletion: option, // Store the option to be deleted
      },
      showDeleteOptionModal: true,
    }));
  };

  closeDeleteOptionModal = () => {
    this.setState({
      showDeleteOptionModal: false,
    });
  };

  handleDeleteOptionSubmit = (selectedOption) => {
    const { sendUpdateCustomFieldRequest } = this.props;
    const { actionCustomField } = this.state;

    // Removing the selected option for deletion and migrating data
    const newOptions = actionCustomField?.options?.filter(
      (option) => option !== actionCustomField?.selectedOptionForDeletion,
    );

    sendUpdateCustomFieldRequest(actionCustomField.id, {
      ...actionCustomField,
      metadata: {
        options: newOptions,
      },
      migrateToOption: selectedOption,
    });

    this.closeDeleteOptionModal();
    this.hideUpdateProspectCustomFieldModal();
  };

  setActiveTabKey = (activeTabKey: string) => {
    this.setState({
      activeTabKey,
    });
  };

  onDeleteCustomFieldSubmit = () => {
    const { sendDeleteCustomFieldRequest } = this.props;
    const { actionCustomField } = this.state;
    sendDeleteCustomFieldRequest(actionCustomField.id);
  };

  componentDidMount = () => {
    this.getFields();
  };

  getFields = (forceRefetch = false) => {
    const {
      customField,
      defaultField,
      sendGetCustomFieldRequest,
      sendGetDefaultFieldRequest,
      customFieldPagination: {
        options: { limit: cL, page: cP },
      },
      defaultFieldPagination: {
        options: { limit: dL, page: dP },
      },
    } = this.props;
    const { activeTabKey } = this.state;
    if (activeTabKey === FieldContentTabs.CustomFields) {
      sendGetCustomFieldRequest({ limit: cL, page: cP });
    } else if (
      activeTabKey === FieldContentTabs.SystemFields &&
      (defaultField.length <= 0 || forceRefetch)
    ) {
      sendGetDefaultFieldRequest({ limit: dL, page: dP });
    }
  };

  getPageNumber = () => {
    const {
      customField,
      customFieldPagination: {
        options: { page: cP },
      },
    } = this.props;

    if (cP > 1 && customField.length === 1) {
      return cP - 1;
    }

    return cP;
  };

  createProspectCustomFieldRefresh = () => {
    const {
      createCustomFieldRequestStatus,
      createCustomFieldRequestMessage,
      createCustomFieldRequestError,
      sendGetCustomFieldRequest,
      customFieldPagination: {
        options: { limit: cL, page: cP },
      },
    } = this.props;

    if (createCustomFieldRequestStatus === RequestStatus.Succeeded) {
      toaster.success(createCustomFieldRequestMessage);
      this.hideCreateProspectCustomFieldModal();
      sendGetCustomFieldRequest({ limit: cL, page: cP });
    }
    if (
      createCustomFieldRequestStatus === RequestStatus.Failed &&
      createCustomFieldRequestError
    ) {
      toaster.error(createCustomFieldRequestError.message);
    }
  };

  updateProspectCustomFieldRefresh = () => {
    const {
      updateCustomFieldRequestStatus,
      updateCustomFieldRequestError,
    } = this.props;

    if (updateCustomFieldRequestStatus === RequestStatus.Succeeded) {
      this.showUpdateFieldSuccessToaster();
      this.hideUpdateProspectCustomFieldModal();
      this.getFields(true);
    }

    if (
      updateCustomFieldRequestStatus === RequestStatus.Failed &&
      updateCustomFieldRequestError
    ) {
      toaster.error(updateCustomFieldRequestError.message);
    }
  };

  showUpdateFieldSuccessToaster = () => {
    const { actionCustomField } = this.state;
    const { t } = this.props;
    if (actionCustomField?.isDefault) {
      toaster.success(t('messages.system_field_update_successfully'));
    } else {
      toaster.success(t('messages.custom_field_update_successfully'));
    }
  };

  deleteCustomFieldRefresh = () => {
    const {
      deleteCustomFieldRequestStatus,
      deleteCustomFieldRequestMessage,
      deleteCustomFieldRequestError,
      sendGetCustomFieldRequest,
      customFieldPagination: {
        options: { limit: cL },
      },
      removeSelectedField,
    } = this.props;

    const { actionCustomField } = this.state;

    if (deleteCustomFieldRequestStatus === RequestStatus.Succeeded) {
      toaster.success(deleteCustomFieldRequestMessage);
      removeSelectedField(actionCustomField?.id?.toString());
      this.hideDeleteCustomFieldModal();
      sendGetCustomFieldRequest({ limit: cL, page: this.getPageNumber() });
    }

    if (
      deleteCustomFieldRequestStatus === RequestStatus.Failed &&
      deleteCustomFieldRequestError
    ) {
      toaster.error(deleteCustomFieldRequestError.message);
    }
  };

  render() {
    const {
      customField,
      defaultField,
      getCustomFieldRequestStatus,
      getDefaultFieldRequestStatus,
      createCustomFieldRequestStatus,
      updateCustomFieldRequestStatus,
      deleteCustomFieldRequestStatus,
      sendGetCustomFieldRequest,
      sendGetDefaultFieldRequest,
      customFieldPagination,
      defaultFieldPagination,
      subscriptionPlan,
    } = this.props;

    const {
      actionCustomField,
      showCreateProspectCustomFieldModal,
      showUpdateProspectCustomFieldModal,
      showDeleteCustomFieldModal,
      activeTabKey,
      showDeleteOptionModal,
    } = this.state;

    const isCreateRequestPending =
      createCustomFieldRequestStatus === RequestStatus.Pending;

    const isUpdateRequestPending =
      updateCustomFieldRequestStatus === RequestStatus.Pending;

    const isDeleteRequestPending =
      deleteCustomFieldRequestStatus === RequestStatus.Pending;

    const options = actionCustomField?.options || [];

    return (
      <div className="custom-fields-container">
        <div className="custom-fields-grid bs-mb-30">
          <CustomFieldHeader headerName="Prospect Fields" />
          <CustomFieldContent
            isRequestPending={
              getIsRequestPending(getCustomFieldRequestStatus) ||
              getIsRequestPending(getDefaultFieldRequestStatus)
            }
            sendGetCustomFieldsRequest={sendGetCustomFieldRequest}
            sendGetDefaultFieldsRequest={sendGetDefaultFieldRequest}
            customFields={customField}
            defaultFields={defaultField}
            customFieldPagination={customFieldPagination}
            defaultFieldPagination={defaultFieldPagination}
            onCreateHandler={this.showCreateProspectCustomFieldModal}
            onUpdateHandler={this.showUpdateProspectCustomFieldModal}
            onDeleteHandler={this.showProspectCustomFieldDeleteModal}
            subscriptionPlan={subscriptionPlan}
            activeTabKey={activeTabKey}
            setActiveTabKey={this.setActiveTabKey}
          />
        </div>
        {showCreateProspectCustomFieldModal && (
          <CreateProspectCustomFieldModal
            show={showCreateProspectCustomFieldModal}
            onClose={this.hideCreateProspectCustomFieldModal}
            onSave={this.onCreateProspectCustomFieldSubmit}
            isRequestPending={isCreateRequestPending}
            onDeleteOptionClick={this.showDeleteOptionModal}
          />
        )}
        {showUpdateProspectCustomFieldModal && (
          <UpdateProspectCustomFieldModal
            show={showUpdateProspectCustomFieldModal}
            onClose={this.hideUpdateProspectCustomFieldModal}
            onSave={this.onUpdateProspectCustomFieldSubmit}
            isRequestPending={isUpdateRequestPending}
            customField={actionCustomField}
            onDeleteOptionClick={this.showDeleteOptionModal}
          />
        )}
        {showDeleteOptionModal && (
          <DeleteOptionModal
            show={showDeleteOptionModal}
            options={options}
            optionToDelete={actionCustomField?.selectedOptionForDeletion}
            onClose={this.closeDeleteOptionModal}
            onSubmit={this.handleDeleteOptionSubmit}
            isRequestPending={isUpdateRequestPending}
          />
        )}
        {showDeleteCustomFieldModal && (
          <DeleteCustomFieldModal
            title="Do you want to delete this custom field?"
            content=" If you delete the custom field, it can hinder the personalization of
            emails."
            show={showDeleteCustomFieldModal}
            onClose={this.hideDeleteCustomFieldModal}
            onSubmit={this.onDeleteCustomFieldSubmit}
            isRequestPending={isDeleteRequestPending}
          />
        )}
      </div>
    );
  }
}

export default withTranslation()(CustomField);
